// The two Whole room stops that are not items (docs/job-view-brief.md, section 2): the job route and the Job BOM, the
// job's own lines from Job setup (the group of kind 'job'). Read here; typed on Job setup. On the walk one check covers
// the job's own setup: the database's Verify of the job row, a person's act.

import type { Resolution, WalkStop } from '@zerogap/engine';
import { refresh, rpc, toast, type Row } from '../../../lib/api';
import { qty } from '../../../lib/format';
import { useSession } from '../../../lib/session';
import { Chain, Money } from '../../../ui/kit';
import { AiChangedTag } from '../../../ui/unverified';
import type { JobCtx } from '../Workspace';
import { JumpBack, mmdd } from './ItemPanel';
import { unitWords, type WalkModel } from './model';

export function JobStopPanel({ ctx, model, mode, stop, res, users, onBack, onChecked }: { ctx: JobCtx; model: WalkModel; mode: 'items' | 'quick' | 'walk'; stop: WalkStop; res: Resolution; users: Row[]; onBack: () => void; onChecked: (key: string) => void }) {
  const s = useSession();
  const lib = ctx.r.lib!;
  const canEdit = s.canEdit('screen.item') && !ctx.locked;
  const g = res.groups.find((x) => x.kind === 'job');
  const route = stop.kind === 'job_route';
  const job = ctx.data.job!;
  const state = model.stateOf(stop);
  const room = model.walls.find((w) => !w.view);
  const list = room?.stops ?? [];
  const at = list.indexOf(stop);
  const steps = g?.steps ?? [];
  const bom = g?.bom ?? [];
  const minutes = steps.reduce((n, x) => n + Number(x.minutes ?? 0), 0);
  const cents = route ? steps.reduce((n, x) => n + x.cents, 0) : bom.reduce((n, x) => n + x.cents, 0);
  const lit = ctx.lit && (ctx.lit.kind === 'route' || ctx.lit.kind === 'bom') ? ctx.lit.key : null;

  const check = async () => {
    await rpc('verify_row', { p_table: 'job', p_id: job.id });
    refresh('job');
    toast(`The job's own setup is checked.`);
    onChecked(stop.key);
  };

  return (
    <div className="walkpanel">
      {ctx.lit && <JumpBack ctx={ctx} />}
      <div className="back">
        <button type="button" className="btn read" onClick={onBack}>
          ‹ Back to list
        </button>
        <span className="where">
          {at + 1} of {list.length} in Whole room
        </span>
      </div>
      <div className="ph">
        <div className="t">
          <span className="no">{route ? 'Route' : 'BOM'}</span>
          <b>{route ? `Job route, ${steps.length} ${steps.length === 1 ? 'step' : 'steps'}` : 'Job BOM'}</b>
          <span className="sp" />
          {job.unverified_since && <AiChangedTag table="job" row={job as any} users={users as any[]} />}
        </div>
        <div className="sub">
          <span>{route ? 'the job type’s steps and what this job typed against them' : 'job-wide lines, not on an item'}</span>
          <span>·</span>
          <span>typed on Job setup</span>
        </div>
        <div className="money">
          <b>
            <Money cents={cents} />
          </b>
          {route && <span>{Math.round(minutes)} min</span>}
          <span>at cost</span>
        </div>
      </div>
      <div className="sect">
        {route ? (
          <table className="lt route">
            <colgroup>
              <col />
              <col style={{ width: 70 }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: 80 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Step</th>
                <th className="r">Qty</th>
                <th>Unit</th>
                <th className="r">Minutes</th>
              </tr>
            </thead>
            <tbody>
              {steps.map((st) => (
                <tr key={st.key} className={lit && st.key.includes(lit) ? 'lit' : ''}>
                  <td>
                    {st.operation_name}
                    {st.labor_class_id && <small className="s"> · {lib.laborClasses[st.labor_class_id]?.name}</small>}
                    <Chain left expr={st.qty_expr} trace={st.trace} note={st.note ?? undefined} />
                  </td>
                  <td className="r">{qty(st.qty)}</td>
                  <td className="s">{unitWords(st.unit)}</td>
                  <td className="r">
                    <span className="lk">{Math.round(Number(st.minutes) * 10) / 10}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="lt bom">
            <colgroup>
              <col />
              <col style={{ width: 110 }} />
              <col style={{ width: 90 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Line</th>
                <th className="r">Qty</th>
                <th className="r">Cost</th>
              </tr>
            </thead>
            <tbody>
              {bom.map((b) => (
                <tr key={b.key} className={lit && b.key.includes(lit) ? 'lit' : ''}>
                  <td>
                    {b.description}
                    <Chain left expr={b.qty_expr} trace={b.trace} note={b.note ?? undefined} />
                  </td>
                  <td className="r">
                    {qty(b.qty)} {b.unit ?? ''}
                  </td>
                  <td className="r">
                    <Money cents={b.cents} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="only">Typed on Job setup, where a line is changed or added. One check covers the job route and the Job BOM: the job's own setup.</p>
      </div>
      <div className="checkbar">
        {mode === 'items' ? (
          <span className="nx">Job setup is where these lines are typed; an AI edit there leaves the job's setup unverified until it is checked here or there.</span>
        ) : state === 'checked' ? (
          <span className="nx">
            <span className="tag ok">checked</span> {mmdd(job.checked_at)} · {users.find((u) => u.id === job.checked_by)?.name ?? 'Spencer'}: the job route and the Job BOM.
          </span>
        ) : (
          canEdit && (
            <>
              <button type="button" className="btn bigcheck" onClick={() => void check()}>
                Check the job's own setup · route and Job BOM
              </button>
              <span className="nx">Then the next stop opens.</span>
            </>
          )
        )}
      </div>
    </div>
  );
}
